import { computed, ref } from 'vue';

import { currentLocale } from '@/plugins/i18n';
import { authService } from '@/services/auth.service';
import { modulesService } from '@/services/modules.service';
import { projectsService } from '@/services/projects.service';
import { useCommunityStore } from '@/store/communityStore';
import type { Module } from '@/store/module.interface';
import { parseProject, type DTOProject, type Project } from '@/store/project.interface';
import { useUserStore } from '@/store/userStore';
import type { StoredFile } from '@/store/common.interface';

export type AccessState = {
  allowed: boolean;
  reason?: string;
};

export function useProjectPage(slug: string, isTemplate: boolean) {
  const communityStore = useCommunityStore();
  const userStore = useUserStore();

  const isLoading = ref(true);
  const showNoAccessDialog = ref(false);
  const requestedProject = ref<Project | null>(null);

  const storeProject = computed<Project | null>(() => {
    const projects = communityStore.community?.projects;
    if (!projects) return null;
    return projects.find((p) => p.slug === slug) ?? null;
  });

  const project = computed<Project | null>(() => storeProject.value ?? requestedProject.value);

  const modules = computed<Module[]>(() => project.value?.modules ?? []);

  const banner = computed<StoredFile | null>(() => {
    if (project.value?.bannerFile?.url) {
      return project.value.bannerFile;
    }
    return communityStore.community?.bannerFile ?? null;
  });

  // TODO: check logic
  const userAccess = computed<AccessState>(() => {
    if (!project.value) return { allowed: false, reason: 'login' };
    return communityStore.isUserAllowedAccessProject(project.value, userStore.user);
  });

  // TODO: check logic
  const isLocaleSupported = computed(() => {
    if (!project.value?.languageEnabled?.length) {
      return true;
    }
    return project.value.languageEnabled.includes(currentLocale());
  });

  const themeKey = computed(
    () => project.value?.themeKey || communityStore.community?.defaultProjectThemeKey || 'default',
  );

  const resolveProject = (response: DTOProject | null | undefined) => {
    if (storeProject.value) {
      requestedProject.value = null;
      return storeProject.value;
    }

    if (!response || typeof response !== 'object' || !('id' in response) || !('slug' in response)) {
      requestedProject.value = null;
      return null;
    }

    requestedProject.value = parseProject(response);
    return requestedProject.value;
  };

  const loadProject = async () => {
    try {
      isLoading.value = true;
      showNoAccessDialog.value = false;

      let response: DTOProject | null | undefined;
      if (isTemplate) {
        response = await projectsService.getProjectTemplateBySlug(slug);
      } else {
        response = await projectsService.getProjectBySlug(slug);
      }

      const resolvedProject = resolveProject(response);
      if (!resolvedProject) {
        return;
      }

      if (!isTemplate && !userAccess.value.allowed) {
        if (userAccess.value.reason === 'login') {
          await authService.login({ project: { slug: resolvedProject.slug } });
          return;
        }

        showNoAccessDialog.value = true;
        return;
      }

      await modulesService.getProjectModules(resolvedProject.id);
    } catch (error) {
      console.error('Failed to load project:', error);
    } finally {
      isLoading.value = false;
    }
  };

  return {
    project,
    modules,
    banner,
    themeKey,
    isLocaleSupported,
    showNoAccessDialog,
    isLoading,
    currentLocale: computed(() => currentLocale()),
    userAccess,
    homeLink: computed(() => userStore.isUserLogged),
    needLogin: computed(() => !userStore.isUserLogged),
    loadProject,
  };
}
